/**
 * Theme preference (design-system.md §2.5), mirroring apps/web/src/lib/theme.ts:
 * `light` | `dark` | `system`. Stored in localStorage under a portal-specific key;
 * `system` (the first-load default) leaves `data-theme` unset so the `prefers-color-scheme`
 * block in styles.css applies. Setting the attribute on `<html>` is the ONLY side effect —
 * every token flips off it.
 *
 * The portal deliberately carries no zustand (vite.config.ts: React + Vite only), so the
 * store is a tiny external-store + `useSyncExternalStore` with the same `useTheme` shape
 * the web app exposes (`useTheme((s) => s.theme)` / `useTheme((s) => s.setTheme)`).
 */
import { useSyncExternalStore } from 'react';

export type ThemePreference = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'argus.portal.theme';

function isPreference(value: unknown): value is ThemePreference {
  return value === 'light' || value === 'dark' || value === 'system';
}

function readInitial(): ThemePreference {
  try {
    const stored = globalThis.localStorage?.getItem(STORAGE_KEY);
    return isPreference(stored) ? stored : 'system';
  } catch {
    return 'system';
  }
}

function persist(theme: ThemePreference): void {
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, theme);
  } catch {
    /* private mode / disabled storage — in-memory only */
  }
}

/** Reflect the preference on `<html data-theme>`; `system` removes the attribute. */
export function applyTheme(theme: ThemePreference): void {
  const root = globalThis.document?.documentElement;
  if (!root) return;
  if (theme === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', theme);
}

/** What `system` currently resolves to (used for the toggle icon). */
export function resolveTheme(theme: ThemePreference): 'light' | 'dark' {
  if (theme !== 'system') return theme;
  return globalThis.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

interface ThemeState {
  theme: ThemePreference;
  setTheme: (theme: ThemePreference) => void;
}

// ── minimal external store ──────────────────────────────────────────────────
let state: ThemeState = {
  theme: readInitial(),
  setTheme: (theme) => {
    persist(theme);
    applyTheme(theme);
    state = { ...state, theme };
    for (const listener of listeners) listener();
  },
};
const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): ThemeState {
  return state;
}

/** Select from the theme store (same call shape as the web app's zustand hook). */
export function useTheme<T>(selector: (s: ThemeState) => T): T {
  return selector(useSyncExternalStore(subscribe, getSnapshot, getSnapshot));
}

useTheme.getState = (): ThemeState => state;
useTheme.setState = (partial: Partial<Pick<ThemeState, 'theme'>>): void => {
  state = { ...state, ...partial };
  for (const listener of listeners) listener();
};

/** Call once at boot so the stored preference paints before first render. */
export function initTheme(): void {
  applyTheme(useTheme.getState().theme);
}
